import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Col,
  Row,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "Components/Common/TableContainer";
import BreadCrumb from "Components/Common/BreadCrumb";

import { getSortObj } from "common/lib/tableDataOptions";
import {
  buildListParams,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";
import * as moment from "moment";
import Permission from "Components/Common/Permission";
import {
  getPaymentCategories,
  deletePaymentCategory,
} from "common/services/payments.service";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
import AddPaymentCategory from "./Add";
import UpdatePaymentCategory from "./Update";
import DeleteModal from "Components/Common/DeleteModal";
import Permissions from "Components/Common/Permissions";
import BulkActionBar from "Components/Common/BulkActionBar";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";

const defaultSort = [{ id: "createdAt", desc: true }];

const PaymentCategories = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: parsePage(searchParams),
    pageSize: parsePageSize(searchParams),
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => parseSort(searchParams.get("sort"), defaultSort));

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const showAddModalForm = () => {
    setIsAddModalOpen(true);
  };

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModalForm = (data: any) => {
    setSelectedRecord(data);
    setIsUpdateModalOpen(true);
  };

  const fetchData = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
  };

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);
  const unCheckAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll") as HTMLInputElement;
    if (checkall) checkall.checked = false;
    document
      .querySelectorAll<HTMLInputElement>(".tableCheckbox")
      .forEach((checkbox) => {
        checkbox.checked = false;
      });
    setSelectedCheckBox([]);
  }, []);

  const onSelectCheckbox = () => {
    setSelectedCheckBox(
      document.querySelectorAll<HTMLInputElement>(".tableCheckbox:checked")
    );
  };

  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll") as HTMLInputElement;
    document
      .querySelectorAll<HTMLInputElement>(".tableCheckbox")
      .forEach((checkbox) => {
        checkbox.checked = checkall.checked;
      });
    onSelectCheckbox();
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
    }) => {
      unCheckAll();
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];
      andConditions.push({
        $or: [
          {
            name: {
              $containsi: searchValue,
            },
          },
        ],
      });

      try {
        setLoading(true);

        const response: any = await getPaymentCategories({
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: ["updatedby"],
          filters: {
            $and: andConditions,
          },
        });
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deletePaymentCategory(selectedRecord?.id);
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Payment category deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting payment category", {});
      } finally {
        setDeleting(false);
      }
    }
  };

  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map(
      (checkbox: any) => checkbox.value
    );
    if (!selectedIds.length) return;

    setDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => deletePaymentCategory(id)));
      unCheckAll();
      fetchUpdatedData();
      setDeleteMultipleModal(false);
      toast.success("Payment categories deleted successfully");
    } catch (e: any) {
      toast.error("Error while deleting payment categories");
    } finally {
      setDeleting(false);
    }
  };

  const { userProfile } = useProfile();
  const columnVisibility = useMemo(
    () => ({
      checkBoxColumn: isAllowed(userProfile?.permissions, [
        { resource: "payment-category", action: "delete" },
      ]),
    }),
    [userProfile?.permissions]
  );

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={checkedAll}
          />
        ),
        cell: (cell: any) => (
          <input
            type="checkbox"
            className="tableCheckbox form-check-input"
            value={cell.getValue()}
            onChange={onSelectCheckbox}
          />
        ),
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="payment-category" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm({
                        id: rowData.id,
                        ...rowData,
                      });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="payment-category" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, onClickDelete]
  );
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Payment Categories"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={8}>
                  <h5 className="card-title">Payment Categories</h5>
                  <p className="mb-0 text-muted">
                    Payment/Salary Categories e.g. Advisor Salary, Top Up, etc.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Search payment categories..."
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          setPage(1);
                        }}
                        value={searchValue}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                    <Permission resource="payment-category" action="create">
                      <button
                        className="btn btn-primary"
                        onClick={() => showAddModalForm()}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add
                      </button>
                    </Permission>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />
              <AddPaymentCategory
                toggle={() => setIsAddModalOpen((state) => !state)}
                isModalOpen={isAddModalOpen}
                fetchData={fetchData}
              />
              <UpdatePaymentCategory
                toggle={() => setIsUpdateModalOpen((state) => !state)}
                isModalOpen={isUpdateModalOpen}
                selectedRecord={selectedRecord}
                fetchData={fetchUpdatedData}
              />
              <DeleteModal
                show={deleteModal}
                onDeleteClick={() => handleDelete()}
                onCloseClick={() => setDeleteModal(false)}
                loading={deleting}
              />
              <DeleteModal
                show={deleteMultipleModal}
                onDeleteClick={handleDeleteMultiple}
                onCloseClick={() => setDeleteMultipleModal(false)}
                loading={deleting}
              />
            </CardBody>
          </Card>
        </Container>
        <Permissions
          checks={[{ resource: "payment-category", action: "delete" }]}
        >
          <BulkActionBar
            count={selectedCheckBox.length}
            onClear={unCheckAll}
            itemLabel="payment category"
            itemLabelPlural="payment categories"
          >
            <Button
              color="danger"
              size="sm"
              className="material-shadow-none"
              onClick={() => setDeleteMultipleModal(true)}
            >
              <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
            </Button>
          </BulkActionBar>
        </Permissions>
      </div>
    </React.Fragment>
  );
};

export default PaymentCategories;
